import React, { useEffect, useState } from 'react';
import { Card, Divider, Input, Radio, Select, Space, Tag, Typography } from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const STRING_SLOT_REGEX = /^string\d+$/;

const isStringSlotValue = (value) => (
  typeof value === 'string' && STRING_SLOT_REGEX.test(value.trim())
);

const createInitialFieldState = (field) => {
  const currentValue = field.currentValue || '';
  const originalValue = field.originalValue || '';
  const defaultStringSlot = field.defaultStringSlot || 'string1';

  if (isStringSlotValue(currentValue)) {
    return {
      mode: 'user_input',
      fixedText: originalValue,
      selectedUserString: currentValue.trim()
    };
  }

  return {
    mode: 'fixed',
    fixedText: currentValue,
    selectedUserString: defaultStringSlot
  };
};

const StringVariableNode = ({ node, onNodeChange }) => {
  const [fieldStates, setFieldStates] = useState({});

  useEffect(() => {
    const nextFieldStates = (node.variableFields || []).reduce((result, field) => {
      result[field.fieldKey] = createInitialFieldState(field);
      return result;
    }, {});

    setFieldStates(nextFieldStates);
  }, [node]);

  const handleTypeChange = (field, newType) => {
    setFieldStates((prev) => {
      const currentFieldState = prev[field.fieldKey] || createInitialFieldState(field);
      const nextFieldState = {
        ...currentFieldState,
        mode: newType
      };

      onNodeChange(node.nodeId, {
        [field.fieldKey]: newType === 'user_input'
          ? nextFieldState.selectedUserString
          : nextFieldState.fixedText
      });

      return {
        ...prev,
        [field.fieldKey]: nextFieldState
      };
    });
  };

  const handleFixedTextChange = (field, value) => {
    setFieldStates((prev) => {
      const currentFieldState = prev[field.fieldKey] || createInitialFieldState(field);
      const nextFieldState = {
        ...currentFieldState,
        fixedText: value
      };

      if (nextFieldState.mode === 'fixed') {
        onNodeChange(node.nodeId, {
          [field.fieldKey]: value
        });
      }

      return {
        ...prev,
        [field.fieldKey]: nextFieldState
      };
    });
  };

  const handleUserStringChange = (field, value) => {
    setFieldStates((prev) => {
      const currentFieldState = prev[field.fieldKey] || createInitialFieldState(field);
      const nextFieldState = {
        ...currentFieldState,
        selectedUserString: value
      };

      if (nextFieldState.mode === 'user_input') {
        onNodeChange(node.nodeId, {
          [field.fieldKey]: value
        });
      }

      return {
        ...prev,
        [field.fieldKey]: nextFieldState
      };
    });
  };

  const variableFields = node.variableFields || [];

  return (
    <Card
      title={`文字变量节点 (ID: ${node.nodeId})`}
      style={{ marginBottom: 16 }}
    >
      <Space style={{ marginBottom: 12 }}>
        <Tag color="blue">字段数 {variableFields.length}</Tag>
        {node.promptType && <Tag color="purple">{node.promptType}</Tag>}
      </Space>

      {node.content ? (
        <div style={{ marginBottom: 16, padding: 8, background: '#fafafa', borderRadius: 6 }}>
          <Text type="secondary">
            当前内容预览: {node.content.length > 120 ? `${node.content.slice(0, 120)}...` : node.content}
          </Text>
        </div>
      ) : null}

      {variableFields.map((field, index) => {
        const fieldState = fieldStates[field.fieldKey] || createInitialFieldState(field);
        const currentValue = fieldState.mode === 'user_input'
          ? fieldState.selectedUserString
          : fieldState.fixedText;

        return (
          <div key={field.fieldKey}>
            {index > 0 && <Divider />}

            <div style={{ marginBottom: 12 }}>
              <Space>
                <Text strong>{field.label}</Text>
                <Tag>{field.fieldKey}</Tag>
                {field.defaultStringSlot && <Tag color="green">默认 {field.defaultStringSlot}</Tag>}
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Radio.Group
                value={fieldState.mode}
                onChange={(e) => handleTypeChange(field, e.target.value)}
              >
                <Radio value="fixed">固定字符串</Radio>
                <Radio value="user_input">用户文字变量</Radio>
              </Radio.Group>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>原始内容</div>
              <div
                style={{
                  minHeight: 72,
                  padding: 12,
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {field.originalValue || '无原始内容'}
              </div>
            </div>

            {fieldState.mode === 'fixed' ? (
              <TextArea
                value={fieldState.fixedText}
                onChange={(e) => handleFixedTextChange(field, e.target.value)}
                placeholder={`输入${field.label}`}
                autoSize={{ minRows: 3, maxRows: 8 }}
              />
            ) : (
              <Select
                style={{ width: '100%' }}
                value={fieldState.selectedUserString}
                onChange={(value) => handleUserStringChange(field, value)}
              >
                {Array.from({ length: 15 }, (_, i) => i + 1).map((num) => (
                  <Option key={num} value={`string${num}`}>
                    文字 {num}
                  </Option>
                ))}
              </Select>
            )}

            <div style={{ marginTop: 12 }}>
              <Text type="secondary">
                当前保存值: {currentValue || '空'}
              </Text>
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 16 }}>
        <Text type="secondary">
          每个字符串字段都支持直接保存文本，或保存为 string1 到 string15 这类用户文字变量占位值。
        </Text>
      </div>
    </Card>
  );
};

export default StringVariableNode;
